import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { DashboardSubject } from '../subjects-card/subjects-card.component';

/**
 * Carte matière à deux actions.
 *
 * <p>Écran d'origine : « Toutes mes matières » (Examens / Devoirs). Le module de
 * test réutilise la même carte avec ses propres actions (Teste-toi / Test
 * diagnostique) — d'où les libellés, liens et pastilles paramétrables plutôt
 * qu'une seconde carte au rendu presque identique, qui divergerait.</p>
 */
@Component({
  selector: 'app-subject-action-card',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './subject-action-card.component.html',
  styleUrl: './subject-action-card.component.css',
})
export class SubjectActionCardComponent {
  @Input({ required: true }) subject!: DashboardSubject;

  /** Destination de chaque action : chaîne simple ou segments de route. */
  @Input() examsLink: string | unknown[] = '/student/tests/exam';
  @Input() homeworkLink: string | unknown[] = '/student/homework';
  @Input() examsLabelKey: string = 'SUBJECTS.EXAMS';
  @Input() homeworkLabelKey: string = 'SUBJECTS.HOMEWORK';
  @Input() examsIcon: string = 'fa-solid fa-graduation-cap';
  @Input() homeworkIcon: string = 'fa-solid fa-clipboard-list';

  /**
   * Paramètres de requête ajoutés aux deux liens. `null` les supprime : les
   * routes du module de test portent déjà la matière dans leur chemin, y
   * répéter son nom en query string n'apporterait rien.
   */
  @Input() queryParams: Record<string, unknown> | null | undefined = undefined;

  /**
   * Pastille de chaque action : ce qui attend l'élève derrière le bouton.
   * `null` ou `0` ne rend rien — une pastille à zéro est du bruit.
   */
  @Input() examsBadge: number | null = null;
  @Input() homeworkBadge: number | null = null;

  /**
   * Visibilité de chaque action : une page dédiée à une seule d'entre elles
   * (« Test diagnostique ») masque l'autre plutôt que de la dupliquer.
   */
  @Input() showExams = true;
  @Input() showHomework = true;

  /**
   * Étoile de mise en favori. Masquée par défaut : seuls les écrans où l'élève
   * gère ses propres matières l'affichent.
   */
  @Input() showFavorite = false;
  @Input() isFavorite = false;

  /** Bascule demandée — l'appelant persiste et remonte le nouvel état. */
  @Output() favoriteToggled = new EventEmitter<boolean>();

  toggleFavorite(event: Event): void {
    // La carte entière est cliquable : l'étoile ne doit pas déclencher la navigation.
    event.preventDefault();
    event.stopPropagation();
    this.favoriteToggled.emit(!this.isFavorite);
  }

  /** Query params effectifs : par défaut la matière, comportement d'origine. */
  get resolvedQueryParams(): Record<string, unknown> | null {
    if (this.queryParams !== undefined) return this.queryParams;
    return { subject: this.subject.i18nKey ?? this.subject.name };
  }
}
